import { useEffect, useState } from "react";
import axios from "axios";
import { DesktopTower, DeviceMobile, Eye, FloppyDisk, Plus, RocketLaunch, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import FormRunner from "@/components/timesheet/form/FormRunner";
import { Badge, Card, act, fmtStamp, useLoad } from "./shared";
import QuestionEditor from "./QuestionEditor";

const ordinalUp = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "TH" : { 1: "ST", 2: "ND", 3: "RD" }[n % 10] || "TH"}`;

function Preview({ schema, onClose }) {
  const [w, setW] = useState("mobile");
  const [ctx, setCtx] = useState(null);
  const [n, setN] = useState(0);
  useEffect(() => { axios.get(`${API}/public/timesheet/form`).then(({ data }) => setCtx(data)); }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="form-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
          <DialogDescription>Nothing is saved from the preview.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant={w === "mobile" ? "default" : "outline"} onClick={() => setW("mobile")} data-testid="form-preview-mobile"><DeviceMobile size={14} className="mr-1" />Mobile</Button>
          <Button size="sm" variant={w === "desktop" ? "default" : "outline"} onClick={() => setW("desktop")} data-testid="form-preview-desktop"><DesktopTower size={14} className="mr-1" />Desktop</Button>
        </div>
        <div className="mx-auto overflow-hidden rounded-2xl border border-slate-200 relative transition-[width] duration-200" style={{ width: w === "mobile" ? 390 : "100%", height: 640 }}>
          {ctx && <FormRunner key={`${w}-${n}`} compact schema={schema} employees={ctx.employees} jobs={ctx.jobs} onSubmit={async (a) => { setTimeout(() => setN((x) => x + 1), 4000); return { employee: a.name, work_date: a.work_date }; }} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function History({ items, canRestore, onRestore }) {
  return (
    <div className="space-y-2" data-testid="form-history">
      {items.map((v) => (
        <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm">
          <span className="font-semibold">v{v.version}</span>
          {v.status === "published" ? <Badge tone="green">Live</Badge> : <Badge>Archived</Badge>}
          <span className="text-xs text-slate-500">published {fmtStamp(v.published_at)} by {v.published_by}{v.notes ? ` · ${v.notes}` : ""}</span>
          {canRestore && <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => onRestore(v.id)} data-testid={`form-restore-${v.version}`}>Start draft from this</Button>}
        </div>
      ))}
    </div>
  );
}

export default function FormManagementTab() {
  const { data, reload } = useLoad("/timesheet/form/versions");
  const [draft, setDraft] = useState(null);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const [newKind, setNewKind] = useState("choice");
  useEffect(() => { setDraft(data?.draft?.schema || null); setNotes(data?.draft?.notes || ""); setDirty(false); }, [data]);

  const edit = (fn) => { setDraft((d) => fn(d)); setDirty(true); };
  const qs = draft?.questions || [];
  const setQ = (i) => (q) => edit((d) => ({ ...d, questions: d.questions.map((x, k) => (k === i ? q : x)) }));
  const move = (i, dir) => edit((d) => { const a = [...d.questions]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; return { ...d, questions: a }; });
  const remove = (i) => edit((d) => ({ ...d, questions: d.questions.filter((_, k) => k !== i) }));
  const add = () => edit((d) => ({ ...d, questions: [...d.questions, { id: `q_${Date.now().toString(36)}`, kind: newKind, title: "New question", helper: "", required: false, options: newKind === "choice" ? ["Yes", "No"] : [], show_if: null }] }));

  const start = async (version_id) => { if (await act(() => api.post("/timesheet/form/draft", { version_id }), "Draft started")) reload(); };
  const save = async () => {
    const clean = { ...draft, questions: draft.questions.map((q) => ({ ...q, options: (q.options || []).map((o) => o.trim()).filter(Boolean) })) };
    const r = await act(() => api.put("/timesheet/form/draft", { schema: clean, notes }), "Draft saved");
    if (r) { setDraft(r.schema); setDirty(false); }
    return r;
  };
  const publish = async () => {
    if (dirty && !(await save())) return;
    if (!window.confirm("Publish this draft? Employees will see the new form right away. Past submissions keep their original answers.")) return;
    if (await act(() => api.post("/timesheet/form/publish"), "New form version is live")) reload();
  };
  const discard = async () => { if (window.confirm("Discard this draft?") && (await act(() => api.delete("/timesheet/form/draft"), "Draft discarded"))) reload(); };

  const live = data?.published;
  const maxJobs = draft?.max_jobs || qs.filter((q) => q.kind === "jobsite").length || 4;
  const setMaxJobs = (n) => {
    n = Math.max(1, Math.min(20, Number(n) || 1));
    if (n === maxJobs) return;
    const dropped = qs.filter((q) => q.kind === "jobsite" && Number(q.id.slice(4)) > n);
    if (dropped.length && !window.confirm(`Lowering to ${n} removes ${dropped.map((q) => `“${q.title}”`).join(", ")} from this draft (and any edits to them). Past submissions are not affected. Continue?`)) return;
    edit((d) => {
      const keep = d.questions.filter((q) => !(q.kind === "jobsite" && Number(q.id.slice(4)) > n));
      const lastJob = keep.reduce((at, q, i) => (q.kind === "jobsite" || q.id === "location_count" ? i : at), 0);
      const have = new Set(keep.map((q) => q.id));
      const add = [];
      for (let k = 1; k <= n; k++) if (!have.has(`job_${k}`)) add.push({ id: `job_${k}`, kind: "jobsite", title: `${ordinalUp(k)} JOBSITE WORKED TODAY`, helper: `(Lugar de trabajo ${k}, Local de trabalho ${k})`, required: true, show_if: { q: "location_count", op: "gte", value: String(k) } });
      const out = [...keep.slice(0, lastJob + 1), ...add, ...keep.slice(lastJob + 1)].map((q) => (q.id === "location_count" ? { ...q, options: Array.from({ length: n + 1 }, (_, i) => String(i)) } : q));
      return { ...d, max_jobs: n, questions: out };
    });
  };
  return (
    <div className="space-y-4">
      <Card title="Form Management" testId="form-mgmt"
        subtitle={live ? `Live: version ${live.version} · published ${fmtStamp(live.published_at)} by ${live.published_by}` : "Loading…"}
        right={
          <div className="flex flex-wrap gap-2">
            {live && <Button variant="outline" size="sm" onClick={() => setPreview(live.schema)} data-testid="form-preview-live-btn"><Eye size={14} className="mr-1" />Preview live form</Button>}
            {data && !data.draft && <Button size="sm" onClick={() => start(null)} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="form-start-draft-btn">Edit form (new draft)</Button>}
          </div>
        }>
        {!draft || !data?.draft ? (
          <p className="text-sm text-slate-500" data-testid="form-no-draft">Changes are made in a draft, previewed, then published. The live form was imported from the original Typeform: Name → Date worked → Locations (0–4) → one jobsite question per location.</p>
        ) : (
          <div className="space-y-3" data-testid="form-draft-editor">
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Badge tone="amber">Draft v{data.draft.version}</Badge> based on v{data.draft.based_on}{dirty && " · unsaved changes"}
            </div>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#327de3]/30 bg-[#327de3]/[0.05] px-3 py-2.5" data-testid="form-max-jobs">
              <label className="text-sm font-semibold text-slate-800" htmlFor="form-max-jobs-input">Maximum jobs/locations in this form</label>
              <div className="flex items-center gap-1">
                <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setMaxJobs(maxJobs - 1)} disabled={maxJobs <= 1} data-testid="form-max-jobs-dec">−</Button>
                <Input id="form-max-jobs-input" type="number" min={1} max={20} value={maxJobs} onChange={(e) => setMaxJobs(e.target.value)} className="h-8 w-16 text-center" data-testid="form-max-jobs-input" />
                <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setMaxJobs(maxJobs + 1)} disabled={maxJobs >= 20} data-testid="form-max-jobs-inc">+</Button>
              </div>
              <p className="text-xs text-slate-500 basis-full">Changing this regenerates the jobsite steps in the draft: “How many locations” offers 0–{maxJobs}, and an answer of N shows exactly N jobsite questions.</p>
            </div>
            {qs.map((q, i) => <QuestionEditor key={q.id} q={q} i={i} all={qs} onChange={setQ(i)} onMove={move} onRemove={remove} />)}
            <div className="flex flex-wrap items-center gap-2">
              <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value={newKind} onChange={(e) => setNewKind(e.target.value)} data-testid="form-new-kind">
                <option value="choice">Multiple choice</option><option value="text">Short text</option><option value="number">Number</option>
              </select>
              <Button variant="outline" size="sm" onClick={add} data-testid="form-add-question-btn"><Plus size={14} className="mr-1" />Add question</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input value={draft.thank_you?.title || ""} onChange={(e) => edit((d) => ({ ...d, thank_you: { ...d.thank_you, title: e.target.value } }))} placeholder="Thank-you title" data-testid="form-thanks-title" />
              <Input value={draft.thank_you?.message || ""} onChange={(e) => edit((d) => ({ ...d, thank_you: { ...d.thank_you, message: e.target.value } }))} placeholder="Thank-you message" data-testid="form-thanks-message" />
            </div>
            <Input value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} placeholder="What changed in this version? (shown in history)" data-testid="form-notes" />
            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={save} disabled={!dirty} data-testid="form-save-draft-btn"><FloppyDisk size={14} className="mr-1" />Save draft</Button>
              <Button variant="outline" size="sm" onClick={() => setPreview(draft)} data-testid="form-preview-draft-btn"><Eye size={14} className="mr-1" />Preview</Button>
              <Button size="sm" onClick={publish} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="form-publish-btn"><RocketLaunch size={14} className="mr-1" />Publish</Button>
              <Button variant="ghost" size="sm" onClick={discard} className="text-rose-600 ml-auto" data-testid="form-discard-btn"><Trash size={14} className="mr-1" />Discard draft</Button>
            </div>
          </div>
        )}
      </Card>
      <Card title="Version history" testId="form-history-card">
        <History items={data?.history || []} canRestore={data && !data.draft} onRestore={start} />
      </Card>
      {preview && <Preview schema={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
